import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { RichHeading } from '@/components/common/Heading';
import { PostCard } from '@/components/layout/NavigationBar/PostCard/PostCard';
import { Section } from '@/components/layout/Section';
import { Stack } from '@/components/layout/Stack';

import type { SanityCaseStudiesSettingsType } from '@/types/sanity';
import type { BlogPostPreview, CaseStudyType } from '@/types/shared';

export interface RelatedStoriesProps {
  fallbackImage: SanityCaseStudiesSettingsType['featuredImageDefault'];
  relatedSection: CaseStudyType['relatedSection'];
}

export const RelatedStories = ({
  fallbackImage,
  relatedSection: { stories, heading },
}: RelatedStoriesProps) => {
  const urlPredicate = (
    postType: BlogPostPreview['_type'] | CaseStudyType['_type'],
  ) => {
    switch (postType) {
      case 'blogPost':
        return '/blog/';
      case 'caseStudy':
        return '/case-studies/';
      default:
    }
  };

  return (
    <Section className="related-stories" spacing="xl">
      <Stack spacing="--space-block-lg">
        {heading && (
          <HeadingWrapper>
            <RichHeading size="heading-lg" heading={heading} />
          </HeadingWrapper>
        )}
        <Columns isEvenPostsNum={stories.length % 2 === 0}>
          {stories.map((post) => (
            <StyledPostCard
              key={post._id}
              title={post.title}
              titleSize="heading-sm"
              url={urlPredicate(post._type) + post.slug.current}
              image={post.featuredImage ?? fallbackImage}
              category={
                post._type === 'blogPost'
                  ? post.category[0].name
                  : 'Case study・' + post.client
              }
              categoryUrl={
                post._type === 'blogPost' &&
                `/blog/category/${post.category[0].slug?.current}`
              }
            />
          ))}
        </Columns>
      </Stack>
    </Section>
  );
};

const HeadingWrapper = styled.div`
  text-align: center;
`;

const StyledPostCard = styled(PostCard)`
  ${screen.md} {
    max-width: 302px;
  }
  max-height: 283px;
  img {
    max-height: 160px;
  }
  p:last-child {
    -webkit-line-clamp: 2;
  }
`;

const Columns = styled.div<{ isEvenPostsNum: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  gap: var(--space-block-md);

  ${screen.sm} {
    display: grid;
    grid-template-columns: repeat(4, 1fr);

    ${StyledPostCard}:nth-child(odd) {
      grid-column-start: 1;
      grid-column-end: span 2;
    }

    ${StyledPostCard}:nth-child(even) {
      grid-column-start: 3;
      grid-column-end: span 2;
    }

    ${({ isEvenPostsNum }) =>
      !isEvenPostsNum &&
      `${StyledPostCard}:last-child {
      grid-column-start: 2;
      grid-column-end: span 2;
    }`}
    ${screen.md} {
      display: grid;
      grid-template-columns: repeat(4, 2fr);
      align-self: center;
      max-width: 60%;
    }
    ${screen.lg} {
      max-width: 100%;
    }
  }

  ${screen.lg} {
    display: flex;
    flex-direction: row;
  }
`;
